// One published workflow: what it does, its versions (any of which can be played, read and
// downloaded), the canvas simulating a run of the chosen version, and what it needs.
import { ArrowLeft, DownloadSimple, SealCheck } from "@phosphor-icons/react";
import clsx from "clsx";
import { Link, useParams, useSearchParams } from "react-router";

import { ApiError, api } from "../api/client";
import { useVersion, useWorkflow } from "../api/hooks";
import { useTitle } from "../ui/hooks";
import { Failed, Loading } from "../ui/Section";
import { WorkflowIcon } from "../workflows/icons";
import { WorkflowPlayer } from "../workflows/LazyPlayer";
import { longDate } from "./blog/format";
import { NotFound } from "./NotFound";
import { Requirements, Skills } from "./workflows/Requirements";

export function WorkflowDetail() {
  const { slug = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const page = useWorkflow(slug);
  const latest = page.data?.latest.version;
  const asked = Number(params.get("v")) || latest;
  const other = useVersion(slug, asked, asked !== undefined && asked !== latest);
  useTitle(page.data?.workflow.name);

  if (page.isPending) return <div className="wrap"><Loading label="Loading the workflow" /></div>;
  if (page.error instanceof ApiError && page.error.status === 404) return <NotFound />;
  if (!page.data) return <div className="wrap"><Failed what="this workflow" retry={() => void page.refetch()} /></div>;

  const { workflow: w, versions } = page.data;
  const shown = asked === latest ? page.data.latest : other.data;
  const choose = (v: number) => setParams(v === latest ? {} : { v: String(v) }, { replace: true });

  return (
    <>
      <section className="grain border-b border-line">
        <div className="wrap py-10 sm:py-14">
          <Link to="/workflows" className="inline-flex items-center gap-1.5 font-mono text-[12px] uppercase tracking-wider text-ink3 hover:text-ink">
            <ArrowLeft weight="bold" className="h-3.5 w-3.5" aria-hidden /> Marketplace
          </Link>
          <div className="mt-6 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-ink text-paper">
                  <WorkflowIcon icon={w.icon} weight="duotone" className="h-5 w-5" aria-hidden />
                </span>
                <span className="chip">{w.category}</span>
                {w.tags.map((t) => (
                  <span key={t} className="chip">
                    {t}
                  </span>
                ))}
              </div>
              <h1 className="display mt-5 text-[2.8rem] sm:text-[4rem]">{w.name}</h1>
              <p className="lede mt-4">{w.summary}</p>
              <p className="mt-4 inline-flex items-center gap-1.5 text-[14.5px] text-ink2">
                <SealCheck weight="fill" className="h-4 w-4 text-teal" aria-hidden /> Published by <strong className="text-ink">{w.publisher}</strong>
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-3">
              {shown ? (
                <a href={api.downloadUrl(w.slug, shown.version)} className="btn-ink" download>
                  <DownloadSimple weight="bold" className="h-4 w-4" /> Download v{shown.version}
                </a>
              ) : null}
              <Link to="/portable-workflows" className="btn-line">
                What's in the file?
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="wrap py-8">
        <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Versions">
          <span className="kicker me-2">Version</span>
          {versions.map((v) => (
            <button
              key={v.version}
              type="button"
              role="tab"
              aria-selected={v.version === asked}
              onClick={() => choose(v.version)}
              className={clsx("rounded-full border px-3.5 py-1.5 font-mono text-[12.5px] font-semibold transition-colors", v.version === asked ? "border-ink bg-ink text-paper" : "border-line bg-card text-ink2 hover:border-ink")}
            >
              v{v.version}
              {v.version === latest ? " · latest" : ""}
            </button>
          ))}
        </div>
        <div className="card mt-5 h-[560px] overflow-hidden sm:h-[640px]">
          {shown ? <WorkflowPlayer key={shown.version} graph={shown.document.version.definition} demoPath={shown.demo_path} className="h-full" /> : <Loading label="Loading the version" />}
        </div>
        <p className="mt-3 font-mono text-[11.5px] text-ink3">A simulated run. Hover to pause, click any step to see its prompt, tools and guard.</p>
      </section>

      <section className="wrap grid gap-6 pb-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="space-y-6">
          <div className="card p-6 sm:p-8">
            <p className="kicker">What it does</p>
            <p className="mt-3 whitespace-pre-line font-serif text-[1.15rem] leading-relaxed text-ink2">{w.description}</p>
          </div>
          <div className="card p-6 sm:p-8">
            <p className="kicker">Versions</p>
            <ol className="mt-4 space-y-5">
              {versions.map((v) => (
                <li key={v.version} className="grid grid-cols-[3.5rem_1fr] gap-3">
                  <button type="button" onClick={() => choose(v.version)} className={clsx("h-fit rounded-lg border px-2 py-1 font-mono text-sm font-semibold", v.version === asked ? "border-signal text-signal" : "border-line text-ink2 hover:border-ink")}>
                    v{v.version}
                  </button>
                  <div>
                    <p className="font-mono text-[11.5px] uppercase tracking-wider text-ink3">
                      {longDate(v.published_at)} · {v.step_count} steps
                    </p>
                    <p className="mt-1 whitespace-pre-line text-[15px] text-ink2">{v.changelog || "First published version."}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
        <div className="space-y-6">
          {shown ? <Requirements requirements={shown.document.version.requirements} /> : null}
          {shown ? <Skills skills={shown.document.skills} /> : null}
        </div>
      </section>
    </>
  );
}
